import { GridCellExpand, GridCellText } from '@civilgrid/ui/components';
import { formatScheduleOverlapParts } from '@civilgrid/utils/src/intelligence/conflictWindows';
import { type ConflictLayerId, isConflictLayer } from '@civilgrid/utils/src/intelligence/index';
import { type CellContext, flexRender, type Row } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { ConflictFeatureRow, IntelligenceSummary } from '~/common/types';
import { BadgeCell, type SummaryColumn } from './columns';
import { formatPercentConflict } from './utils';

/**
 * Accordion child rows for the Conflicts Summary table: one row per conflict
 * feature whose plan window overlaps the project's construction window.
 */
export type FeatureRow = {
  id: string;
  label: string;
  chipLabel: string;
  chipColor: 'red' | 'yellow' | 'orange';
  overlapStartMs: number;
  overlapEndMs: number;
  overlapArea: number | null;
};

export type SummaryRow = IntelligenceSummary & {
  /** Present on child rows only. */
  featureRow?: FeatureRow;
  subRows?: SummaryRow[];
};

// Same palette as the parent Conflict Detail badges in columns.tsx.
const LAYER_CHIPS: Record<ConflictLayerId, { label: string; color: 'red' | 'yellow' | 'orange' }> = {
  pavingMoratorium: { label: 'Moratoria', color: 'red' },
  pavingProjects: { label: 'Paving', color: 'orange' },
  cipProjects: { label: 'CIP', color: 'yellow' },
};

// Plan-date millis are UTC midnights parsed server-side; UTC year keeps the label zone-stable.
const yearLabel = (ms: number | null | undefined): string =>
  ms == null ? 'Unlisted' : `${new Date(ms).getUTCFullYear()}`;

function toFeatureLabel(feature: ConflictFeatureRow): string {
  const owner = feature.owner || 'Unknown Agency';
  return `${owner}: ${yearLabel(feature.startMs)} - ${yearLabel(feature.endMs)}`;
}

/**
 * The summary's conflict features as accordion rows, in server order (shortest
 * schedule overlap first, then earliest start). Overlap with the construction
 * window is decided server-side; this filters on that verdict.
 */
export function getOverlappingConflictFeatures(summary: IntelligenceSummary): FeatureRow[] {
  const rows: FeatureRow[] = [];
  for (const feature of summary.conflictFeatures ?? []) {
    if (!isConflictLayer(feature.layerId) || !feature.window) continue;
    const chip = LAYER_CHIPS[feature.layerId];

    rows.push({
      id: feature.id,
      label: toFeatureLabel(feature),
      chipLabel: chip.label,
      chipColor: chip.color,
      overlapStartMs: feature.window.overlapStartMs,
      overlapEndMs: feature.window.overlapEndMs,
      overlapArea: feature.overlapArea ?? null,
    });
  }

  return rows;
}

/**
 * Attaches the overlapping features as `subRows` for any summary with at least
 * one. Children spread the parent so untouched accessor code stays safe, with
 * a unique row id.
 */
export function withFeatureSubRows(summaries: IntelligenceSummary[]): SummaryRow[] {
  return summaries.map((summary) => {
    const features = getOverlappingConflictFeatures(summary);
    if (features.length === 0) return summary;

    return {
      ...summary,
      subRows: features.map((featureRow) => ({
        ...summary,
        id: `${summary.id}:feature:${featureRow.id}`,
        featureRow,
        conflictFeatures: undefined,
        subRows: undefined,
      })),
    };
  });
}

function renderChildCell(columnId: string, row: SummaryRow): ReactNode {
  const featureRow = row.featureRow;
  if (!featureRow) return null;

  switch (columnId) {
    case 'project.name':
      return (
        <GridCellText className="font-medium" data-testid={`cell-feature-name-${featureRow.id}`}>
          {featureRow.label}
        </GridCellText>
      );
    case 'conflictDetail':
      return (
        <BadgeCell
          color={featureRow.chipColor}
          label={featureRow.chipLabel}
          data-testid={`cell-feature-detail-${featureRow.id}`}
        />
      );
    case 'scheduleOverlap': {
      const { duration, range } = formatScheduleOverlapParts({
        counts: { moratoria: 0, paving: 0, cip: 0 },
        overlapStartMs: featureRow.overlapStartMs,
        overlapEndMs: featureRow.overlapEndMs,
      });
      return (
        <div className="flex items-center gap-1.5" data-testid={`cell-feature-overlap-${featureRow.id}`}>
          <BadgeCell color="grey" label={duration} />
          <GridCellText>{range}</GridCellText>
        </div>
      );
    }
    case 'conflictArea':
      return (
        <GridCellText data-testid={`cell-feature-area-${featureRow.id}`}>
          {featureRow.overlapArea == null ? '—' : Math.round(featureRow.overlapArea).toLocaleString()}
        </GridCellText>
      );
    case 'percentConflict':
      return (
        <GridCellText data-testid={`cell-feature-percent-${featureRow.id}`}>
          {formatPercentConflict(featureRow.overlapArea, row.rightOfWayArea)}
        </GridCellText>
      );
    default:
      return null;
  }
}

/**
 * Wraps every leaf column's cell for accordion rows: child rows (`depth > 0`)
 * render the per-feature cells above and null everywhere else (children spread
 * their parent, so unwrapped accessors would repeat the parent's values); the
 * parent Project ID cell gains the GridCellExpand chevron. Parent cells render
 * through flexRender so hook-using cell components keep their own boundary.
 */
export function withFeatureChildCells(columns: SummaryColumn[]): SummaryColumn[] {
  return columns.map((column) => {
    const children = (column as { columns?: SummaryColumn[] }).columns;
    if (children) return { ...column, columns: withFeatureChildCells(children) } as SummaryColumn;

    const originalCell = column.cell;
    const renderOriginal = (ctx: CellContext<IntelligenceSummary, unknown>) =>
      typeof originalCell === 'function' ? flexRender(originalCell, ctx) : (ctx.renderValue() as ReactNode);

    return {
      ...column,
      cell: (ctx: CellContext<IntelligenceSummary, unknown>) => {
        const row = ctx.row as Row<SummaryRow>;
        if (row.depth > 0) return renderChildCell(column.id, row.original);

        if (column.id === 'project.projectId') {
          const summary = row.original;
          const onToggle = () => {
            capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryFeatureRowsToggled, {
              project_id: summary.project.id,
              summary_id: summary.id,
              expanded: !row.getIsExpanded(),
              feature_count: row.subRows.length,
            });
            row.toggleExpanded();
          };

          return (
            <GridCellExpand
              canExpand={row.getCanExpand()}
              isExpanded={row.getIsExpanded()}
              onToggle={onToggle}
              toggleTestId={`expand-row-toggle-${summary.project.id}`}
            >
              {renderOriginal(ctx)}
            </GridCellExpand>
          );
        }

        return renderOriginal(ctx);
      },
    } as SummaryColumn;
  });
}
